import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAppSelector } from "../../../../app/store";
import { formatIndianCompactNumber } from "../../../../lib/utils";
import { selectFraudTypeMetricPoints } from "../../logic/dashboardSlice";
import {
  BaseChart,
  chartColorTokens,
  chartPalettes,
  rechartsConfig,
} from "../common/BaseChart";
import ChartEmptyState from "../common/ChartEmptyState";
import ChartSkeleton from "../common/ChartSkeleton";
import CustomTooltip from "../common/customToolTip";
import CustomXAxisTick from "../common/customXAxisTick";
import type { DashboardLoadStateProps } from "./IncidentsByBranchCharts";

const IncidentsByFraudTypeChart = ({
  isLoading,
  error,
}: DashboardLoadStateProps) => {
  const axisLabel = (value: string | undefined | null) =>
    value && value.trim() ? value : "Not specified";

  const points = useAppSelector(selectFraudTypeMetricPoints);
  // Fixed axis reservation to keep chart heights identical across the row.
  const X_AXIS_HEIGHT = 90;
  const data = points
    .slice()
    .sort(
      (a, b) =>
        (b.metrics.total_cases_opened ?? 0) -
        (a.metrics.total_cases_opened ?? 0),
    )
    .map((p) => ({
      type: axisLabel(p.label),
      count: p.metrics.total_cases_opened,
      fraudAmount: p.metrics.total_fraud_amount ?? 0,
    }));

  const emptyState = (() => {
    if (isLoading && points.length === 0) return <ChartSkeleton />;
    if (error) {
      const msg = error ?? "Failed to load dashboard metrics";
      const isAuth = /401|403|unauthor/i.test(msg);
      return (
        <ChartEmptyState
          variant={isAuth ? "not-configured" : "error"}
        />
      );
    }
    if (points.length === 0)
      return (
        <ChartEmptyState
          variant="no-data"
          description="Try adjusting the date range or filters."
        />
      );
    return undefined;
  })();

  return (
    <BaseChart
      title="Incidents by Type of Fraud"
      heightClassName="h-80"
      emptyState={emptyState}
    >
      <BarChart
        data={data}
        margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
        {...rechartsConfig.common}
      >
        <CartesianGrid
          strokeDasharray={rechartsConfig.grid.strokeDasharray}
          stroke={rechartsConfig.grid.stroke}
          strokeOpacity={rechartsConfig.grid.strokeOpacity}
        />
        <XAxis
          dataKey="type"
          angle={-45}
          textAnchor="end"
          height={X_AXIS_HEIGHT}
          tick={<CustomXAxisTick showTooltip maxChars={22} width={120} />}
          axisLine={rechartsConfig.xAxis.axisLine}
          tickLine={rechartsConfig.xAxis.tickLine}
        />
        <YAxis
          yAxisId="left"
          tick={rechartsConfig.yAxis.tick}
          axisLine={rechartsConfig.yAxis.axisLine}
          tickLine={rechartsConfig.yAxis.tickLine}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={rechartsConfig.yAxis.tick}
          axisLine={rechartsConfig.yAxis.axisLine}
          tickLine={rechartsConfig.yAxis.tickLine}
          tickFormatter={(value) => formatIndianCompactNumber(value, { includeCurrency: true })}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="top"
          align="center"
          wrapperStyle={{
            ...rechartsConfig.legend.wrapperStyle,
            top: 0,
            paddingBottom: 10,
          }}
        />
        <Bar
          yAxisId="left"
          dataKey="count"
          fill={chartPalettes.investigation}
          name="Total Cases Opened"
        />
        <Bar
          yAxisId="right"
          dataKey="fraudAmount"
          fill={chartColorTokens.orange}
          name="Total Fraud Amount"
        />
      </BarChart>
    </BaseChart>
  );
};
export default IncidentsByFraudTypeChart;
